import React, { useState } from 'react'
import { Card } from '../../ui'

/**
 * Section-level manager annotation surface.
 * Same visibility rules as InlineManagerNote but rendered below the last card of a section.
 *
 * When `onSuggest` is provided and the note is editable, a sparkle
 * button appears that calls the AI suggestion endpoint.
 */
export function SectionNote({
  value = '',
  onChange,
  onSuggest,
  isManager,
  isShared,
  isReady,
  isClosed,
  reportName = 'the report',
}) {
  const [suggesting, setSuggesting] = useState(false)

  // Employee view, not shared: no DOM
  if (!isManager && !isShared) return null

  // Closed with no content: no DOM
  if (isClosed && !value) return null

  // Employee view, shared but no content: no DOM
  if (!isManager && isShared && !value) return null

  const readOnly = isClosed || !isManager
  const disabled = isManager && !isReady && !isClosed
  const canSuggest = isManager && isReady && !isClosed && !!onSuggest

  async function handleSuggest() {
    if (!onSuggest) return
    setSuggesting(true)
    try {
      const suggestion = await onSuggest()
      if (suggestion) onChange?.(suggestion)
    } finally {
      setSuggesting(false)
    }
  }

  return (
    <Card style={{ background: 'var(--sidebar-bg)', border: '1px solid var(--border)' }}>
      <div style={styles.container}>
        <div style={styles.labelRow}>
          <span style={styles.label}>Manager section note</span>
          {canSuggest && (
            <button
              type="button"
              onClick={handleSuggest}
              disabled={suggesting}
              style={styles.suggestBtn}
              aria-label="Suggest section note with AI"
              title="Ask Claude to suggest a section note"
            >
              {suggesting ? '...' : '✦ Suggest'}
            </button>
          )}
        </div>
        {disabled ? (
          <textarea
            style={{ ...styles.textarea, opacity: 0.5 }}
            disabled
            placeholder={`You can add coaching notes once ${reportName} marks the reflection ready.`}
            rows={2}
            aria-label="Manager section note (disabled)"
          />
        ) : readOnly ? (
          <p style={styles.text}>{value}</p>
        ) : (
          <textarea
            style={styles.textarea}
            value={value}
            onChange={(e) => onChange?.(e.target.value)}
            placeholder="Zoom out. What's the through-line across this section?"
            rows={2}
            aria-label="Manager section note"
          />
        )}
      </div>
    </Card>
  )
}

const styles = {
  container: { display: 'flex', flexDirection: 'column', gap: '4px' },
  labelRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  label: {
    fontSize: '10px',
    fontWeight: 700,
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    color: 'var(--text-secondary)',
  },
  textarea: {
    width: '100%',
    border: 'none',
    outline: 'none',
    background: 'transparent',
    color: 'var(--text-primary)',
    resize: 'vertical',
    lineHeight: 1.6,
    fontSize: '13px',
    fontFamily: 'inherit',
  },
  text: {
    fontSize: '13px',
    color: 'var(--text-primary)',
    lineHeight: 1.6,
    margin: 0,
    whiteSpace: 'pre-wrap',
  },
  suggestBtn: {
    fontSize: '10px',
    fontWeight: 600,
    padding: '2px 8px',
    borderRadius: '999px',
    border: '1px solid var(--border)',
    background: 'transparent',
    color: 'var(--text-secondary)',
    cursor: 'pointer',
  },
}
